import { GAME_DOMAIN, GAME_NAME, MAX_BASE, MAX_TOTAL } from './config';
import { formatTime } from './format';

export type Tier = 'bullseye' | 'green' | 'yellow' | 'orange' | 'red';

/** Tier per question base score (PRD §4.4). */
export function tierFor(basePts: number): Tier {
  if (basePts >= MAX_BASE) return 'bullseye';
  if (basePts >= 80) return 'green';
  if (basePts >= 50) return 'yellow';
  if (basePts >= 20) return 'orange';
  return 'red';
}

export const TIER_EMOJI: Record<Tier, string> = {
  bullseye: '🎯',
  green: '🟢',
  yellow: '🟡',
  orange: '🟠',
  red: '🔴',
};

/** Human labels for colour-blind-safe UI (PRD §10). */
export const TIER_LABEL: Record<Tier, string> = {
  bullseye: 'Bullseye',
  green: 'Great',
  yellow: 'Good',
  orange: 'Close-ish',
  red: 'Miss',
};

/** Hex colours per tier, shared by the map (arc, pins, region halo) and charts. */
export const TIER_COLOR: Record<Tier, string> = {
  bullseye: '#34d399',
  green: '#22c55e',
  yellow: '#facc15',
  orange: '#fb923c',
  red: '#ef4444',
};

/** Tier for a whole-day total (0–1000), scaled to the per-question bands. */
export const tierForTotal = (total: number): Tier => tierFor(Math.round(total / 10));

export const emojiFor = (basePts: number): string => TIER_EMOJI[tierFor(basePts)];

export const emojiRow = (bases: readonly number[]): string => bases.map(emojiFor).join('');

export interface ShareInput {
  puzzleNo: number;
  /** 'daily' (default) shares the puzzle number; replays and random rounds say so instead. */
  mode?: 'daily' | 'archive' | 'random';
  total: number;
  bases: readonly number[];
  timeMs: number;
  streak: number;
  gameName?: string;
  domain?: string;
  /** A themed special: its title replaces the puzzle number and the link opens the special. */
  special?: { slug: string; title: string } | null;
}

/**
 * Share text (PRD §4.4), shaped like the other daily games' pastes so it sits naturally next to
 * them in a group chat: title and score, emoji row, extras, link last. A daily score links to its
 * "beat my score" challenge page, so every paste is also an invite with a preview card.
 *
 *     [GAME_NAME] #42 · 873/1,000
 *     🟢🟡🎯🟢🟠
 *     ⏱ 1:12 · 🔥 5-day streak
 *     [DOMAIN]/challenge/42/873
 */
export function buildShareText({
  puzzleNo,
  mode = 'daily',
  total,
  bases,
  timeMs,
  streak,
  gameName = GAME_NAME,
  domain = GAME_DOMAIN,
  special = null,
}: ShareInput): string {
  const score = `${total.toLocaleString('en-GB')}/${MAX_TOTAL.toLocaleString('en-GB')}`;
  if (special) {
    return [
      `${gameName} · ${special.title} · ${score}`,
      emojiRow(bases),
      `⏱ ${formatTime(timeMs)} · special`,
      `${domain}/specials/${special.slug}`,
    ].join('\n');
  }
  const title =
    mode === 'random'
      ? `${gameName} random round · ${score}`
      : mode === 'archive'
        ? `${gameName} #${puzzleNo} (replay) · ${score}`
        : `${gameName} #${puzzleNo} · ${score}`;
  // A 1-day streak is just "played today"; show it from day 2.
  const meta =
    mode === 'daily'
      ? [`⏱ ${formatTime(timeMs)}`, ...(streak >= 2 ? [`🔥 ${streak}-day streak`] : [])].join(' · ')
      : `⏱ ${formatTime(timeMs)} · practice`;
  const link = mode === 'daily' ? `${domain}/challenge/${puzzleNo}/${total}` : domain;
  return [title, emojiRow(bases), meta, link].join('\n');
}
